import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import type * as React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { ErrorHandler } from "@/web/lib/errors";
import {
  cardExitEase,
  commitProgressDefault,
  motionDurations,
  motionTransitions,
} from "@/web/lib/motion-tokens";
import { cn } from "@/web/lib/styling";

export type SwipeDirection = "left" | "right";

export type SwipableSwipeEvent = {
  direction: SwipeDirection;
  x: number;
  velocity: number;
  reset: () => void;
};

/**
 * Imperative handle for `Swipable`. `swipe(direction)` commits a swipe
 * programmatically — it runs the exact same exit + `onSwipeComplete` lifecycle
 * as a pointer drag, just without any pointer movement. Used by the e2e
 * walkthrough (via a hidden dev-only button) to bypass flaky pointer-timing
 * dependent drag simulation; not intended for app code.
 */
export type SwipableHandle = {
  swipe: (direction: SwipeDirection) => void;
};

export type SwipableBehaviorProps = {
  disabled?: boolean;
  /**
   * Which directions commit a swipe. Defaults to both.
   *
   * Restricting this does more than ignore the unwanted direction — it also
   * suppresses the drag *feedback* on that side (no elastic give, no fade, no
   * rotate), so the card visibly refuses to move that way instead of animating
   * as if it were about to commit and then snapping back. A spring-back reads
   * as "the app rejected my input"; a card that barely budges reads as "that
   * direction isn't a thing here", which is what we want.
   */
  directions?: readonly SwipeDirection[];
  /**
   * Who animates the card off screen once a swipe commits.
   *
   * - `"animate"` (default) — `Swipable` owns it: the card travels
   *   `exitDistance` and fades, pausing at `commitProgress` for async work.
   *   Right for cards that unmount on commit.
   * - `"handoff"` — the *caller* owns it. The handler fires the instant the
   *   drag is released, then `x` snaps straight back to origin with no exit
   *   tween and no reset spring, so whatever the caller animates instead (a
   *   parent wrapper, a layout transition) is the only motion on screen.
   *
   * Use `"handoff"` whenever something outside `Swipable` already animates the
   * card away. Two transforms racing on the same element don't just look
   * doubled — `reset()`'s spring is underdamped and inherits the exit tween's
   * residual velocity, so it overshoots *past* origin and leaves the card
   * visibly offset in the opposite direction from the swipe.
   */
  exitMode?: "animate" | "handoff";
  threshold?: number;
  velocityThreshold?: number;
  exitDistance?: number;
  commitProgress?: number;
  onSwipeLeft?: (event: SwipableSwipeEvent) => void | Promise<void>;
  onSwipeRight?: (event: SwipableSwipeEvent) => void | Promise<void>;
  onSwipeCancel?: () => void;
  onSwipePendingChange?: (isPending: boolean) => void;
  onSwipeComplete?: (event: SwipableSwipeEvent) => void;
};

type MotionDivProps = React.ComponentProps<typeof motion.div>;

export type SwipableProps = Omit<
  MotionDivProps,
  | "children"
  | "drag"
  | "dragConstraints"
  | "dragElastic"
  | "dragMomentum"
  | "onDragEnd"
  | "onDragStart"
> &
  SwipableBehaviorProps & {
    children: React.ReactNode;
  };

const DEFAULT_THRESHOLD = 96;
const DEFAULT_VELOCITY_THRESHOLD = 650;
const DEFAULT_EXIT_MULTIPLIER = 1.35;
const DEFAULT_DIRECTIONS: readonly SwipeDirection[] = ["left", "right"];
const DEFAULT_DRAG_ELASTIC = 0.18;

export const Swipable = forwardRef<SwipableHandle, SwipableProps>(function Swipable(
  {
    children,
    className,
    disabled = false,
    directions = DEFAULT_DIRECTIONS,
    exitMode = "animate",
    threshold = DEFAULT_THRESHOLD,
    velocityThreshold = DEFAULT_VELOCITY_THRESHOLD,
    exitDistance,
    commitProgress = commitProgressDefault,
    onSwipeLeft,
    onSwipeRight,
    onSwipeCancel,
    onSwipePendingChange,
    onSwipeComplete,
    style,
    ...props
  }: SwipableProps,
  ref,
) {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<{ stop: () => void } | null>(null);
  const [isExiting, setIsExiting] = useState(false);

  const allowsLeft = directions.includes("left");
  const allowsRight = directions.includes("right");

  const x = useMotionValue(0);
  const opacityRange = useMemo(() => [-threshold * 1.75, 0, threshold * 1.75], [threshold]);
  const rotateRange = useMemo(() => [-threshold * 2, 0, threshold * 2], [threshold]);
  // Drag feedback is clamped to the permitted directions: a disallowed side
  // holds at the resting opacity/rotate so it never looks primed to commit.
  const opacityOutput = useMemo(
    () => [allowsLeft ? 0.18 : 1, 1, allowsRight ? 0.18 : 1],
    [allowsLeft, allowsRight],
  );
  const rotateOutput = useMemo(
    () => [allowsLeft ? -7 : 0, 0, allowsRight ? 7 : 0],
    [allowsLeft, allowsRight],
  );
  const opacity = useTransform(x, opacityRange, opacityOutput);
  const rotate = useTransform(x, rotateRange, rotateOutput);
  // Elastic give is likewise one-sided. `dragConstraints` pins both edges to 0,
  // so an elasticity of 0 on a side means the card simply does not travel that
  // way under the pointer.
  const dragElastic = useMemo(
    () => ({
      left: allowsLeft ? DEFAULT_DRAG_ELASTIC : 0,
      right: allowsRight ? DEFAULT_DRAG_ELASTIC : 0,
      top: 0,
      bottom: 0,
    }),
    [allowsLeft, allowsRight],
  );

  useEffect(
    () => () => {
      animationRef.current?.stop();
    },
    [],
  );

  const reset = useCallback(() => {
    animationRef.current?.stop();
    onSwipePendingChange?.(false);
    if (reduceMotion) {
      // Imperative `animate()` doesn't pick up MotionConfig's `reducedMotion` —
      // skip the spring and snap straight to origin under reduced motion.
      x.set(0);
      animationRef.current = null;
    } else {
      animationRef.current = animate(x, 0, motionTransitions.cardSwipeReset);
    }
    setIsExiting(false);
  }, [onSwipePendingChange, reduceMotion, x]);

  const runExit = useCallback(
    (direction: SwipeDirection, xValue: number, velocity: number) => {
      const isAllowed = direction === "left" ? allowsLeft : allowsRight;
      const handler = direction === "left" ? onSwipeLeft : onSwipeRight;
      if (!isAllowed || handler == null) {
        reset();
        return;
      }

      const width = rootRef.current?.offsetWidth ?? threshold * 3;
      const distance = exitDistance ?? width * DEFAULT_EXIT_MULTIPLIER;
      const target = direction === "left" ? -distance : distance;
      const clampedCommitProgress = Math.min(Math.max(commitProgress, 0), 1);
      const event: SwipableSwipeEvent = {
        direction,
        x: xValue,
        velocity,
        reset,
      };

      // Handoff: fire the handler now and snap `x` home in the same tick. No
      // exit tween and no reset spring, so the caller's own animation is the
      // only thing moving. `x.set` rather than `animate` is the whole point —
      // an animated return would still be in flight while the caller animates,
      // and its residual velocity is what makes the reset spring overshoot.
      if (exitMode === "handoff") {
        // Stop any in-flight reset spring from a previous cancelled drag before
        // taking the value over. `isExiting` is necessarily false here — every
        // path into `runExit` is guarded on it — so there's nothing to unwind.
        animationRef.current?.stop();
        animationRef.current = null;
        x.set(0);
        try {
          const result = handler(event);
          Promise.resolve(result)
            .then(() => onSwipeComplete?.(event))
            .catch((err: unknown) => ErrorHandler.logError(err));
        } catch (err: unknown) {
          ErrorHandler.logError(err);
        }
        return;
      }

      const finishExit = () => {
        const handleComplete = () => {
          animationRef.current = null;
          onSwipeComplete?.(event);
        };

        if (clampedCommitProgress >= 1) {
          handleComplete();
          return;
        }

        animationRef.current?.stop();
        animationRef.current = animate(x, target, {
          type: "tween",
          duration: motionDurations.cardExitS * (1 - clampedCommitProgress),
          ease: cardExitEase,
          onComplete: handleComplete,
        });
      };

      const commitSwipe = () => {
        onSwipePendingChange?.(true);

        try {
          const result = handler(event);
          Promise.resolve(result)
            .then(() => {
              onSwipePendingChange?.(false);
              finishExit();
            })
            .catch((err: unknown) => {
              ErrorHandler.logError(err);
              onSwipePendingChange?.(false);
              reset();
            });
        } catch (err: unknown) {
          ErrorHandler.logError(err);
          onSwipePendingChange?.(false);
          reset();
        }
      };

      if (reduceMotion) {
        commitSwipe();
        return;
      }

      setIsExiting(true);
      animationRef.current?.stop();

      const commitTarget = xValue + (target - xValue) * clampedCommitProgress;
      const commitDuration = motionDurations.cardExitS * clampedCommitProgress;

      if (commitDuration === 0) {
        commitSwipe();
        return;
      }

      animationRef.current = animate(x, commitTarget, {
        type: "tween",
        duration: commitDuration,
        ease: cardExitEase,
        onComplete: () => {
          animationRef.current = null;
          commitSwipe();
        },
      });
    },
    [
      allowsLeft,
      allowsRight,
      commitProgress,
      exitDistance,
      exitMode,
      onSwipeComplete,
      onSwipeLeft,
      onSwipePendingChange,
      onSwipeRight,
      reduceMotion,
      reset,
      threshold,
      x,
    ],
  );

  // Expose an imperative `swipe(direction)` so callers (the e2e walkthrough's
  // hidden button) can commit a swipe without a pointer drag. Reuses `runExit`
  // directly — same reducer + exit + `onSwipeComplete` path as a real gesture.
  useImperativeHandle(
    ref,
    () => ({
      swipe: (direction: SwipeDirection) => {
        if (disabled || isExiting) return;
        runExit(direction, 0, 0);
      },
    }),
    [disabled, isExiting, runExit],
  );

  const hasSwipeHandler =
    (allowsLeft && onSwipeLeft != null) || (allowsRight && onSwipeRight != null);
  const canDrag = !disabled && hasSwipeHandler;
  const isInteractive = canDrag && !isExiting;

  return (
    <motion.div
      ref={rootRef}
      data-slot="swipable"
      data-disabled={!canDrag}
      data-exiting={isExiting}
      className={cn(
        isInteractive && "cursor-grab touch-pan-y active:cursor-grabbing",
        isExiting && "pointer-events-none",
        className,
      )}
      style={{ ...style, x, opacity, rotate }}
      drag={canDrag ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={dragElastic}
      dragMomentum={false}
      dragSnapToOrigin={false}
      onDragStart={() => {
        // Sanity-stop any in-flight reset/exit animation before the user takes
        // over the motion value. Motion's drag controller normally preempts
        // animations on the same value, but a fresh drag should always begin
        // from a stable state — direction-flips during a half-finished reset
        // would otherwise carry stale momentum.
        animationRef.current?.stop();
        animationRef.current = null;
      }}
      onDragEnd={(_event, info) => {
        if (!canDrag || isExiting) {
          return;
        }

        const xValue = x.get();
        const velocity = info.velocity.x;
        const distancePassed = Math.abs(xValue) >= threshold;
        const velocityPassed = Math.abs(velocity) >= velocityThreshold;

        if (!distancePassed && !velocityPassed) {
          onSwipeCancel?.();
          reset();
          return;
        }

        runExit(xValue < 0 || velocity < 0 ? "left" : "right", xValue, velocity);
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
});
